import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileSpreadsheet,
  FileText,
  Folder,
  Hash,
  Link as LinkIcon,
  NotebookText,
  Pin,
  Presentation,
  type LucideIcon,
} from 'lucide-react';
import { formatDayMonth } from '../../utils/dateFormat';
import { PageShell, PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { TabBar, type TabItem } from '../../components/ui/Tabs';
import { useAccess } from '../../hooks/useAccess';
import { useAuth } from '../../hooks/useAuth';
import { useDeleteResourceLink, useResourceMeta, useResources } from '../../hooks/useResources';
import { LinkEditor } from './LinkEditor';
import { CampaignMaterials } from './CampaignMaterials';
import type { ResourceLink, ResourceSection } from '../../api/resources';

/** Matched against title, description, group, source and search terms. */
function matches(link: ResourceLink, query: string): boolean {
  if (!query) return true;
  const haystack = [
    link.title,
    link.description ?? '',
    link.category ?? '',
    link.source,
    link.url,
    ...link.tags,
  ]
    .join(' ')
    .toLowerCase();
  // Every word must appear somewhere, so "mrr sheet" narrows rather than widens.
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

const STALE_AFTER_DAYS = 180;

/** Brands that write their own name in lowercase. */
const LOWERCASE_BRANDS = /\blemlist\b/gi;

/** "google_drive" → "Google drive"; "lemlist" stays lowercase, as the brand writes it. */
function humanizeSource(source: string): string {
  const words = source.replace(/[_-]+/g, ' ').trim();
  return (words.charAt(0).toUpperCase() + words.slice(1)).replace(LOWERCASE_BRANDS, 'lemlist');
}

/** What kind of thing a link opens, read from its URL and source. */
function linkType(link: ResourceLink): { icon: LucideIcon; label: string } {
  const url = link.url.toLowerCase();
  const source = link.source.toLowerCase();
  if (url.includes('/spreadsheets/') || /\.(xlsx?|csv)(\?|$)/.test(url)) return { icon: FileSpreadsheet, label: 'Sheet' };
  if (url.includes('/presentation/') || source.includes('pitch') || /\.(pptx?|key)(\?|$)/.test(url))
    return { icon: Presentation, label: 'Deck' };
  if (url.includes('/folders/') || url.includes('/drive/')) return { icon: Folder, label: 'Folder' };
  if (url.includes('/document/') || /\.(docx?|pdf)(\?|$)/.test(url)) return { icon: FileText, label: 'Document' };
  if (source.includes('notion') || url.includes('notion.')) return { icon: NotebookText, label: 'Notion page' };
  if (source.includes('slack') || url.includes('slack.com')) return { icon: Hash, label: 'Slack' };
  return { icon: LinkIcon, label: 'Link' };
}

/** Every link in the list carries the same non-empty value. */
function sharedValue(
  links: ResourceLink[],
  pick: (l: ResourceLink) => string | null,
  minCount = 2,
): string | null {
  if (links.length < minCount || links.length === 0) return null;
  const first = pick(links[0]!)?.trim() || null;
  return first && links.every((l) => (pick(l)?.trim() || null) === first) ? first : null;
}

/** True when a group heading already names the source ("Drive folders" and google_drive). */
function headingNamesSource(heading: string, source: string): boolean {
  if (!heading) return false;
  const words = source.toLowerCase().split(/[_\-\s]+/).filter(Boolean);
  const key = words[words.length - 1];
  return Boolean(key) && heading.toLowerCase().includes(key!);
}

/**
 * One link on one line: title and source chip, then the actions and the
 * update date flush right. Pinned is said once per section, by the "Pinned" block at its top. The URL and description are the title's tooltip; a source
 * or description every row in the group shares is said once in the group header
 * instead, so the source shows in one place only.
 */
function LinkRow({
  link,
  canEdit,
  showSource,
  onEdit,
  onDelete,
}: {
  link: ResourceLink;
  canEdit: boolean;
  showSource: boolean;
  onEdit: (link: ResourceLink) => void;
  onDelete: (link: ResourceLink) => void;
}) {
  const age = daysSince(link.verifiedAt);
  const stale = age !== null && age > STALE_AFTER_DAYS;
  const type = linkType(link);
  const TypeIcon = type.icon;

  return (
    <li className="group flex min-h-[2.75rem] items-center gap-3 py-1.5">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <TypeIcon className="h-4 w-4 flex-none text-text-tertiary" aria-label={type.label} />
        <a
          href={link.url}
          target="_blank"
          rel="noreferrer"
          title={link.description ?? undefined}
          className="min-w-0 truncate text-body-sm font-medium text-text-primary hover:underline"
        >
          {link.title.replace(LOWERCASE_BRANDS, 'lemlist')}
        </a>
        {showSource && (
          <span className="flex-none" title={link.source}>
            <Badge>{humanizeSource(link.source)}</Badge>
          </span>
        )}
      </div>

      <div className="flex flex-none items-center gap-1.5">
        {canEdit && (
          <span className="flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <Button variant="ghost" size="sm" onClick={() => onEdit(link)}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(link)}>
              Remove
            </Button>
          </span>
        )}
        {stale && <Badge variant="warning">Unchecked {age}d</Badge>}
        <span className="w-24 text-right text-body-xs tabular-nums text-text-muted" title="Last updated in the register">
          Updated {formatDayMonth(link.updatedAt)}
        </span>
      </div>
    </li>
  );
}

/** Links of one section, split by their optional group heading. */
function LinkGroups({
  links,
  canEdit,
  onEdit,
  onDelete,
}: {
  links: ResourceLink[];
  canEdit: boolean;
  onEdit: (link: ResourceLink) => void;
  onDelete: (link: ResourceLink) => void;
}) {
  // Ungrouped links first, then each named group alphabetically.
  const groups = new Map<string, ResourceLink[]>();
  for (const link of links) {
    const key = link.category ?? '';
    groups.set(key, [...(groups.get(key) ?? []), link]);
  }
  const orderedKeys = [...groups.keys()].sort((a, b) =>
    a === '' ? -1 : b === '' ? 1 : a.localeCompare(b),
  );

  // Pinned links from every group sit in one "Pinned" block at the top, so
  // the label is said once per section instead of under each group heading.
  const pinned = orderedKeys.flatMap((key) => (groups.get(key) ?? []).filter((l) => l.isPinned));

  return (
    <>
      {pinned.length > 0 && (
        <div>
          <p className="mb-1 flex items-center gap-1 text-body-xs text-text-tertiary">
            <Pin className="h-3 w-3" aria-hidden />
            Pinned
          </p>
          <ul className="divide-y divide-border-light">
            {pinned.map((link) => (
              <LinkRow key={link.id} link={link} canEdit={canEdit} showSource onEdit={onEdit} onDelete={onDelete} />
            ))}
          </ul>
        </div>
      )}
      {orderedKeys.map((key) => {
        const groupLinks = (groups.get(key) ?? []).filter((l) => !l.isPinned);
        if (groupLinks.length === 0) return null;
        // A named group with one source says it once in its header, even for a
        // single link; the unnamed group needs two links to make that worthwhile.
        const source = sharedValue(groupLinks, (l) => l.source, key ? 1 : 2);
        const description = sharedValue(groupLinks, (l) => l.description);
        const note = [source ? humanizeSource(source) : null, description?.replace(/\.$/, '')]
          .filter(Boolean)
          .join(' · ');
        return (
          <div key={key || '_ungrouped'} className="mt-5 first:mt-0">
            {(key || note) && (
              <p className="mb-1 flex flex-wrap items-baseline gap-x-2">
                {key && <span className="eyebrow">{key}</span>}
                {note && <span className="text-body-xs text-text-tertiary">{note}</span>}
              </p>
            )}
            <ul className="divide-y divide-border-light">
              {groupLinks.map((link) => (
                <LinkRow
                  key={link.id}
                  link={link}
                  canEdit={canEdit}
                  showSource={!source && !headingNamesSource(key, link.source)}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

/**
 * The link register. One page for everything that lives in Slack, Notion, Drive
 * or Pitch, so "where is the MRR sheet" is a search rather than a question.
 *
 * Sections are the access-control features themselves and become the tabs across
 * the top: a person only sees the sections they can already reach elsewhere in
 * the OS, and a section they cannot reach has no tab rather than an empty one.
 *
 * Search deliberately ignores the selected tab and looks across every section the
 * user can see — somebody typing "mrr" wants the sheet, not a reminder that they
 * are standing in the wrong tab.
 */
export function Resources() {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAccess();
  const { user } = useAuth();
  // Members may add and edit links too; the API limits them to the sections they
  // can see, which are exactly the tabs on this page. Viewers read only.
  const canEdit = isAdmin || user?.role === 'member';
  const { data, isLoading, error } = useResources();
  const meta = useResourceMeta(isAdmin);
  const remove = useDeleteResourceLink();

  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<ResourceLink | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [addFeature, setAddFeature] = useState<string | undefined>(undefined);

  const sections = useMemo<ResourceSection[]>(() => data?.sections ?? [], [data]);
  const searching = query.trim().length > 0;

  const tabs: TabItem<string>[] = sections.map((s) => ({ id: s.feature, label: s.label }));

  // The tab from the URL when it is one this user actually has, else the first.
  const active =
    section && sections.some((s) => s.feature === section) ? section : sections[0]?.feature;
  const activeSection = sections.find((s) => s.feature === active) ?? null;

  // Every matching link, section by section. Only built while searching.
  const results = useMemo(
    () =>
      sections
        .map((s) => ({ ...s, links: s.links.filter((l) => matches(l, query)) }))
        .filter((s) => s.links.length > 0),
    [sections, query],
  );
  const resultCount = results.reduce((sum, s) => sum + s.links.length, 0);

  const openAdd = (feature?: string) => {
    setEditing(null);
    setAddFeature(feature);
    setEditorOpen(true);
  };

  const openEdit = (link: ResourceLink) => {
    setEditing(link);
    setAddFeature(undefined);
    setEditorOpen(true);
  };

  const onDelete = (link: ResourceLink) => {
    if (!window.confirm(`Remove "${link.title}" from the register?`)) return;
    remove.mutate(link.id);
  };

  return (
    <PageShell>
      <PageHeader eyebrow="Company"
        title="Resources"
        subtitle="Every document, sheet, deck and dashboard, in one place."
        info="You see the sections you have access to. Search looks across all of them at once."
        actions={
          canEdit ? (
            <Button variant="secondary" onClick={() => openAdd(active)}>
              Add link
            </Button>
          ) : undefined
        }
      >
        {/* Searching looks across every section, so the tabs step aside. */}
        {!searching && activeSection && (
          <TabBar
            tabs={tabs}
            active={activeSection.feature}
            onSelect={(feature) => navigate(`/resources/${feature}`)}
          />
        )}
      </PageHeader>

      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search every section — try mrr, pitch deck, board, knowledge base…"
        aria-label="Search resources"
      />

      {remove.error && (
        <Alert variant="error">Could not remove the link: {remove.error.message}</Alert>
      )}

      {isAdmin && meta.data && meta.data.orphaned.length > 0 && (
        <Alert variant="warning" title="Links nobody can see">
          {meta.data.orphaned.length} link(s) are filed under a section that no longer exists, so
          they are served to no one. Edit each one and choose a current section:{' '}
          {meta.data.orphaned.map((l) => l.title).join(', ')}.
        </Alert>
      )}

      {isLoading && <LoadingState />}

      {error && <ErrorState title="Couldn’t load the register" error={error} />}

      {!isLoading && !error && sections.length === 0 && (
        <EmptyState
          title="Nothing in the register yet"
          description={canEdit ? 'Add the first link to get it started.' : 'Somebody with edit rights still has to fill it.'}
        />
      )}

      {!isLoading && !error && sections.length > 0 && searching && (
        <div className="space-y-4">
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="text-body-sm font-medium text-text-secondary">
              {resultCount} {resultCount === 1 ? 'result' : 'results'} for “{query}”
            </h2>
            <Button variant="ghost" size="sm" onClick={() => setQuery('')}>
              Clear search
            </Button>
          </div>

          {resultCount === 0 ? (
            <EmptyState
              title={`Nothing matches “${query}”`}
              description="Try a shorter term, or add the link with the words you searched for."
            />
          ) : (
            results.map((s) => (
              <Card key={s.feature} padding="md">
                <h3 className="mb-1 text-body-sm font-medium text-text-secondary">{s.label}</h3>
                <LinkGroups
                  links={s.links}
                  canEdit={canEdit}
                  onEdit={openEdit}
                  onDelete={onDelete}
                />
              </Card>
            ))
          )}
        </div>
      )}

      {/* Browsing: tabs across the top, one clear section underneath. */}
      {!isLoading && !error && sections.length > 0 && !searching && activeSection && (
        <div className="space-y-4">
          <Card padding="md">
            <p className="mb-1 text-body-xs text-text-tertiary">
              {activeSection.links.length} {activeSection.links.length === 1 ? 'link' : 'links'}
            </p>
            <LinkGroups
              links={activeSection.links}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={onDelete}
            />
          </Card>

          {/* Campaign collateral sits under Sales: it is what a rep sends out,
              and it needs pickers and a copy button rather than a plain list. */}
          {activeSection.feature === 'sales' && <CampaignMaterials isAdmin={isAdmin} />}
        </div>
      )}

      {canEdit && (
        <LinkEditor
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          link={editing}
          defaultFeature={addFeature}
        />
      )}
    </PageShell>
  );
}

export default Resources;
